import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar
} from 'recharts';
import { useDispatch } from 'react-redux';
import { getUsersOverTime, getRevenueOverTime, getJournalEntriesOverTime, getCommunityPostsOverTime } from 'store/actions';

const Analytics = () => {
  const dispatch = useDispatch();
  const [usersData, setUsersData] = useState([]); // [{date, count}]
  const [revenueData, setRevenueData] = useState([]); // [{date, total}]
  const [journalData, setJournalData] = useState([]); // [{date, count}]
  const [communityData, setCommunityData] = useState([]); // [{date, count}]

  useEffect(() => {
    // load 30-day default analytics
    dispatch(getUsersOverTime(30, (data) => {
      setUsersData(data || []);
    }));

    dispatch(getRevenueOverTime(30, (data) => {
      setRevenueData(data || []);
    }));

    dispatch(getJournalEntriesOverTime(30, (data) => {
      // backend returns [{date, count}]
      setJournalData(data || []);
    }));

    dispatch(getCommunityPostsOverTime(30, (data) => {
      // backend returns [{date, count}]
      setCommunityData(data || []);
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Analytics</Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>User Signups (last 30 days)</Typography>
              <Typography variant="body2" color="text.secondary">Daily new users — useful to spot growth or drop-offs.</Typography>
              <Box sx={{ height: 300, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={usersData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#1976d2" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Revenue (last 30 days)</Typography>
              <Typography variant="body2" color="text.secondary">Daily revenue totals (sums of subscription amounts).</Typography>
              <Box sx={{ height: 300, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip formatter={(value) => value && value.toLocaleString()} />
                    <Legend />
                    <Bar dataKey="total" fill="#2e7d32" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Journal Entries (last 30 days)</Typography>
              <Typography variant="body2" color="text.secondary">Number of journal entries created per day.</Typography>
              <Box sx={{ height: 300, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={journalData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#0288d1" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Community Posts (last 30 days)</Typography>
              <Typography variant="body2" color="text.secondary">Number of community posts created per day.</Typography>
              <Box sx={{ height: 300, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={communityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#7b1fa2" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );
};

export default Analytics;
